// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/relatorios/services/relatoriosService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { DirecaoOrdenacao } from '@/lib/consultas';
import type {
  PaginadoResponse,
  RelatorioTitulosPaginado,
  TipoDataTitulo,
  VendaClienteSintetico,
  VendaClienteAnalitico,
  VendaProdutoSintetico,
  VendaProdutoAnalitico,
  VendaFormaPagamento,
  EvolucaoVendas,
  HistoricoCliente,
  CurvaAbcCliente,
  CurvaAbcProduto,
  LucratividadeResponse,
  EvolucaoProduto,
  NotaAnalitico,
  VendasCfop,
  ContaReceberAnalitico,
  ContaReceberSintetico,
  ContaPagarAnalitico,
  ContaPagarSintetico,
  CompraFornecedor,
  CompraFornecedorAnalitico,
} from '../types';

// ── helpers ───────────────────────────────────────────────────────────────────

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return p ? `?${p}` : '';
}

// ── Vendas ────────────────────────────────────────────────────────────────────

const vendas = {
  clientesSintetico: async (p: {
    dtInicio?: string; dtFim?: string; clienteId?: number;
    pagina?: number; tamanhoPagina?: number; ordenarPor?: string; direcao?: DirecaoOrdenacao;
  }): Promise<PaginadoResponse<VendaClienteSintetico>> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/clientes${qs(p)}`);
    return data;
  },

  clientesAnalitico: async (p: {
    dtInicio?: string; dtFim?: string; clienteId?: number;
    pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<VendaClienteAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/clientes/analitico${qs(p)}`);
    return data;
  },

  produtosSintetico: async (p: {
    dtInicio?: string; dtFim?: string; produtoId?: number; grupoId?: number;
    pagina?: number; tamanhoPagina?: number; ordenarPor?: string; direcao?: DirecaoOrdenacao;
  }): Promise<PaginadoResponse<VendaProdutoSintetico>> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/produtos${qs(p)}`);
    return data;
  },

  produtosAnalitico: async (p: {
    dtInicio?: string; dtFim?: string; produtoId?: number; grupoId?: number;
    pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<VendaProdutoAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/produtos/analitico${qs(p)}`);
    return data;
  },

  formasPagamento: async (p: { dtInicio?: string; dtFim?: string }): Promise<VendaFormaPagamento[]> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/formas-pagamento${qs(p)}`);
    return data;
  },

  evolucao: async (p: { dtInicio?: string; dtFim?: string }): Promise<EvolucaoVendas[]> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/evolucao${qs(p)}`);
    return data;
  },

  historicoCliente: async (p: {
    clienteId: number; dtInicio?: string; dtFim?: string;
    pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<HistoricoCliente>> => {
    const { data } = await apiClient.get(`/api/relatorios/vendas/historico-cliente${qs(p)}`);
    return data;
  },
};

// ── Gerencial ─────────────────────────────────────────────────────────────────

const gerencial = {
  abcClientes: async (p: { dtInicio?: string; dtFim?: string }): Promise<CurvaAbcCliente[]> => {
    const { data } = await apiClient.get(`/api/relatorios/gerencial/abc-clientes${qs(p)}`);
    return data;
  },

  abcProdutos: async (p: { dtInicio?: string; dtFim?: string }): Promise<CurvaAbcProduto[]> => {
    const { data } = await apiClient.get(`/api/relatorios/gerencial/abc-produtos${qs(p)}`);
    return data;
  },

  lucratividade: async (p: { dtInicio?: string; dtFim?: string; grupoId?: number }): Promise<LucratividadeResponse> => {
    const { data } = await apiClient.get(`/api/relatorios/gerencial/lucratividade${qs(p)}`);
    return data;
  },

  evolucaoProdutos: async (p: {
    dtInicio?: string; dtFim?: string; produtoId?: number; grupoId?: number;
  }): Promise<EvolucaoProduto[]> => {
    const { data } = await apiClient.get(`/api/relatorios/gerencial/evolucao-produtos${qs(p)}`);
    return data;
  },
};

// ── Fiscais ───────────────────────────────────────────────────────────────────

const fiscais = {
  saidas: async (p: {
    dtInicio?: string; dtFim?: string; clienteId?: number;
    produtoId?: number; cfop?: string; pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<NotaAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/fiscais/saidas${qs(p)}`);
    return data;
  },

  entradas: async (p: {
    dtInicio?: string; dtFim?: string; fornecedorId?: number;
    produtoId?: number; cfop?: string; pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<NotaAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/fiscais/entradas${qs(p)}`);
    return data;
  },

  cfop: async (p: { dtInicio?: string; dtFim?: string; saida?: boolean }): Promise<VendasCfop[]> => {
    const { data } = await apiClient.get(`/api/relatorios/fiscais/cfop${qs(p)}`);
    return data;
  },
};

// ── Financeiro ────────────────────────────────────────────────────────────────

const financeiro = {
  contasReceberAnalitico: async (p: {
    dtVencInicio?: string; dtVencFim?: string; isPago?: boolean; tipoData?: TipoDataTitulo;
    clienteId?: number; pagina?: number; tamanhoPagina?: number;
  }): Promise<RelatorioTitulosPaginado<ContaReceberAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/financeiro/contas-receber${qs(p)}`);
    return data;
  },

  contasReceberSintetico: async (p: {
    dtVencInicio?: string; dtVencFim?: string; isPago?: boolean; tipoData?: TipoDataTitulo;
  }): Promise<ContaReceberSintetico[]> => {
    const { data } = await apiClient.get(`/api/relatorios/financeiro/contas-receber/sintetico${qs(p)}`);
    return data;
  },

  contasPagarAnalitico: async (p: {
    dtVencInicio?: string; dtVencFim?: string; isPago?: boolean; tipoData?: TipoDataTitulo;
    fornecedorId?: number; pagina?: number; tamanhoPagina?: number;
  }): Promise<RelatorioTitulosPaginado<ContaPagarAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/financeiro/contas-pagar${qs(p)}`);
    return data;
  },

  contasPagarSintetico: async (p: {
    dtVencInicio?: string; dtVencFim?: string; isPago?: boolean; tipoData?: TipoDataTitulo;
  }): Promise<ContaPagarSintetico[]> => {
    const { data } = await apiClient.get(`/api/relatorios/financeiro/contas-pagar/sintetico${qs(p)}`);
    return data;
  },

  comprasFornecedor: async (p: {
    dtInicio?: string; dtFim?: string; fornecedorId?: number;
  }): Promise<CompraFornecedor[]> => {
    const { data } = await apiClient.get(`/api/relatorios/financeiro/compras${qs(p)}`);
    return data;
  },

  comprasAnalitico: async (p: {
    dtInicio?: string; dtFim?: string; fornecedorId?: number;
    pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<CompraFornecedorAnalitico>> => {
    const { data } = await apiClient.get(`/api/relatorios/financeiro/compras/analitico${qs(p)}`);
    return data;
  },
};

const relatoriosService = { vendas, gerencial, fiscais, financeiro };
export default relatoriosService;
